/* RESPONSIBLE TEAM: team-automation-foundations */
import Model, { attr } from '@ember-data/model';
import { type State } from 'embercom/models/data/matching-system/matching-constants';
import { buildValidations, validator } from 'ember-cp-validations';
import { states } from 'embercom/models/data/matching-system/matching-constants';
import { tracked } from '@glimmer/tracking';
import { VISUALIZATION_TYPES } from 'embercom/models/reporting/custom/visualization-options';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import {
  MAX_VISIBLE_CHARACTER_COUNT,
  MAX_SERIALIZED_HTML_LENGTH,
  getVisibleCharacterCount,
} from 'embercom/lib/ai-agent/guidance-utils';
import { hasFeature } from 'embercom/helpers/has-feature';

const FEATURE_FLAG_HTML_LENGTH_VALIDATION = 'ai-agent-guidance-html-length-validation';

const Validations = buildValidations({
  prompt: [
    validator('presence', {
      presence: true,
      messageKey: 'ai-agent.guidance.validation-errors.prompt.empty',
    }),
    validator('inline', {
      validate(value: string, _options: unknown, model: Guideline) {
        if (!value) {
          return true; // Let presence validator handle empty case
        }

        let isNewValidationEnabled = hasFeature(
          FEATURE_FLAG_HTML_LENGTH_VALIDATION,
          model.appService,
        );

        if (isNewValidationEnabled) {
          // New validation: check visible character count (decoded HTML)
          let visibleLength = getVisibleCharacterCount(value);
          if (visibleLength > MAX_VISIBLE_CHARACTER_COUNT) {
            return model.intl.t('ai-agent.guidance.validation-errors.prompt.too-long', {
              max: MAX_VISIBLE_CHARACTER_COUNT,
            });
          }
        } else if (value.length > MAX_VISIBLE_CHARACTER_COUNT) {
          // Legacy validation: check raw prompt length
          return model.intl.t('ai-agent.guidance.validation-errors.prompt.too-long', {
            max: MAX_VISIBLE_CHARACTER_COUNT,
          });
        }
        return true;
      },
    }),
    validator('inline', {
      validate(value: string, _options: unknown, model: Guideline) {
        if (!value) {
          return true;
        }
        // Only check serialized HTML limit when feature flag is enabled
        let isNewValidationEnabled = hasFeature(
          FEATURE_FLAG_HTML_LENGTH_VALIDATION,
          model.appService,
        );
        if (isNewValidationEnabled && value.length > MAX_SERIALIZED_HTML_LENGTH) {
          return model.intl.t('ai-agent.guidance.validation-errors.serialized-html-limit-exceeded');
        }
        return true;
      },
    }),
  ],
  title: [
    validator('presence', {
      presence: true,
      ignoreBlank: true,
      messageKey: 'ai-agent.guidance.validation-errors.title.empty',
    }),
    validator('length', { max: 100, min: 1 }),
  ],
});

// Define the Stat enum in the model since we'll need it here
enum Stat {
  USED = 'used',
  RESOLVED = 'resolved',
  ROUTED = 'routed',
}

export default class Guideline extends Model.extend(Validations) {
  @attr('number') declare state: State;
  @attr('string') declare category: GuidelineCategory;
  @attr('string') declare prompt: string;
  @attr('string') declare title: string;
  @attr({ defaultValue: () => [] }) declare tags: GuidelineTag[];
  @attr({ defaultValue: () => [] }) declare aiContentSegmentIds: number[];
  @attr({ defaultValue: () => [] }) declare targetChannels: string[];

  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare appService: AppService;
  @service declare intl: $TSFixMe;

  @tracked assessment: GuidelineAssessment | null = null;
  @tracked promptModified = false;
  @tracked createdFromTemplate = false;
  @tracked _originalPrompt: string | null = null;
  @tracked _originalTitle: string | null = null;

  get hasPrompt() {
    return (this.prompt?.trim()?.length || 0) > 0;
  }

  get hasState() {
    return this.state !== undefined;
  }

  get enabled() {
    return this.state === states.live;
  }

  get serializedBlocks() {
    return this.aiAgentGuidelineService.serializedBlocksFromPrompt(this.prompt);
  }

  get translationCategory(): string {
    return Guideline.categoryToTranslationKey(this.category);
  }

  static categoryToTranslationKey(category: GuidelineCategory): string {
    return category.replace(/_/g, '-');
  }

  get chartDefinitions() {
    return {
      used: this.chartDefinitionForStat(Stat.USED),
      resolved: this.chartDefinitionForStat(Stat.RESOLVED),
      routed: this.chartDefinitionForStat(Stat.ROUTED),
    };
  }

  chartDefinitionForStat(stat: Stat): any {
    let metricId = '';
    let chartId = `${this.id}_${stat}`;

    switch (stat) {
      case Stat.USED:
        metricId = 'fin.conversations_participated.count';
        break;
      case Stat.RESOLVED:
        metricId = 'fin.resolution_rate';
        break;
      case Stat.ROUTED:
        metricId = 'fin.routed_to_team_rate';
        break;
      default:
        throw new Error(`Invalid stat: ${stat}`);
    }

    return {
      id: chartId,
      visualization_type: VISUALIZATION_TYPES.COUNTER,
      chart_series: [
        {
          metric_id: metricId,
          filters: {
            type: 'and',
            filters: [
              {
                type: 'category',
                data: {
                  property: 'fin.guidelines_applied.guideline_ids',
                  attribute: 'conversation.fin.guidelines_applied.guideline_ids',
                  values: [this.id],
                },
              },
            ],
          },
        },
      ],
    };
  }

  setAssessment(data: KeysToSnakeCase<GuidelineAssessment>) {
    this.assessment = {
      hasIssue: data.has_issue || false,
      advice: data.advice || null,
      shouldRewrite: data.should_rewrite || false,
      suggestedRewrite: data.suggested_rewrite || null,
      originalPrompt: this.prompt,
      adviceHeadline: data.advice_headline || null,
    };
  }

  discardAssessment() {
    this.assessment = null;
  }

  get hasChangedPromptOrTitle() {
    return this.prompt !== this._originalPrompt || this.title !== this._originalTitle;
  }
}

export type GuidelineCategory =
  | 'tone'
  | 'clarification'
  | 'routing'
  | 'content'
  | 'spam_detection'
  | 'other'
  | 'copilot'
  | 'voice_routing'
  | 'voice_clarification'
  | 'voice_tone'
  | 'voice_content';

export interface GuidelineAssessment {
  hasIssue: boolean;
  advice: string | null;
  shouldRewrite: boolean;
  suggestedRewrite: string | null;
  originalPrompt: string | null;
  adviceHeadline: string | null;
}

export interface GuidelineTag {
  id: string;
  name: string;
  isSelected: boolean;
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'ai-agent/guidelines': Guideline;
  }
}
